<template>
  <v-dialog v-model="dialog" persistent max-width="800px">
    <v-card ref="form">
      <v-card-title>
        <span class="headline">Asignación de Roles - {{ usuario }}</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-text-field
                outlined
                dense
                v-model="searchRoles"
                append-icon="search"
                label="Buscar roles..."
                single-line
                hide-details
                class="mb-4"
              ></v-text-field>
            </v-col>
          </v-row>
          
          <v-row>
            <v-col cols="12">
              <div class="d-flex mb-3">
                <v-btn
                  color="primary"
                  x-small
                  @click="asignarTodosRoles"
                  :disabled="loading"
                  class="mr-2"
                >
                  <v-icon x-small left>check_circle</v-icon>
                  Asignar Todos
                </v-btn>
                
                <v-btn
                  color="error"
                  x-small
                  @click="quitarTodosRoles"
                  :disabled="loading"
                >
                  <v-icon x-small left>cancel</v-icon>
                  Quitar Todos
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-flex>
              <v-data-table
                dense
                :headers="headers"
                :items="rolesFiltrados"
                :loading="loading"
                class="elevation-1"
                :items-per-page="10"
                :header-props="{
                  'sort-icon': 'fa-solid fa-arrow-down-wide-short',
                }"
                :footer-props="{
                  showFirstLastPage: true,
                  'items-per-page-text': 'Filas por página',
                  'items-per-page-options': [10, 25, 50, -1],
                }"
              >
                <template v-slot:[`item.estado`]="{ item }">
                  <v-chip
                    :color="item.estado === 'Asignado' ? 'success' : 'grey'"
                    small
                    text-color="white"
                  >
                    {{ item.estado }}
                  </v-chip>
                </template>

                <template v-slot:[`item.acciones`]="{ item }">
                  <div style="display: flex; gap: 5px;">
                    <v-btn
                      x-small
                      text
                      :color="item.estado === 'Asignado' ? 'error' : 'success'"
                      :loading="item.loading"
                      @click="asignarRol(item)"
                      :disabled="loading"
                    >
                      <v-icon x-small>
                        {{ item.estado === 'Asignado' ? 'remove_circle' : 'add_circle' }}
                      </v-icon>
                      {{ item.estado === 'Asignado' ? 'Quitar' : 'Asignar' }}
                    </v-btn>
                  </div>
                </template>

                <template v-slot:no-data>
                  <v-btn color="primary" @click="cargarRolesUsuario">
                    Recargar
                  </v-btn>
                </template>
              </v-data-table>
            </v-flex>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-chip
          color="info"
          outlined
          class="mr-2"
        >
          <v-icon left small>check_circle</v-icon>
          Asignados: {{ rolesAsignados }}
        </v-chip>
        
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="cerrarDialogoRoles">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DialogRolesUsuario',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    idUsuario: {
      type: [String, Number],
      default: null
    },
    usuario: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      loading: false,
      searchRoles: '',
      rolesUsuario: [],
      headers: [
        { 
          text: 'Código', 
          value: 'codigo', 
          sortable: true,
          class: "primary white--text text-subtitle-2"
        },
        { 
          text: 'Descripción', 
          value: 'descripcion', 
          sortable: true,
          class: "primary white--text text-subtitle-2"
        },
        { 
          text: 'Estado', 
          value: 'estado', 
          sortable: true,
          class: "primary white--text text-subtitle-2"
        },
        { 
          text: 'Acciones', 
          value: 'acciones', 
          sortable: false, 
          align: 'center',
          class: "primary white--text text-subtitle-2"
        }
      ]
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },

    rolesFiltrados() {
      if (!this.searchRoles) {
        return this.rolesUsuario
      }
      
      const search = this.searchRoles.toLowerCase()
      return this.rolesUsuario.filter(rol => 
        rol.codigo.toLowerCase().includes(search) || 
        rol.descripcion.toLowerCase().includes(search)
      )
    },

    rolesAsignados() {
      return this.rolesUsuario.filter(rol => rol.estado === 'Asignado').length
    }
  },

  watch: {
    dialog(newVal) {
      if (newVal && this.idUsuario) {
        this.cargarRolesUsuario()
      }
    }
  },

  methods: {
    cargarRolesUsuario() {
      if (!this.idUsuario) return
      
      this.loading = true
      let me = this
      
      // Usar la nueva API: ListarRoles
      axios
        .get(`api/Usuarios/ListarRoles/${this.idUsuario}`)
        .then(function (response) {
          me.rolesUsuario = response.data.map(rol => ({
            ...rol,
            id_rol: rol.id,
            nombre: rol.codigo,
            loading: false
          }))
        })
        .catch(function (error) {
          console.error('Error cargando roles:', error)
          me.$store.dispatch('setSnackbar', {
            color: 'error',
            text: 'Error al cargar los roles del usuario'
          })
        })
        .finally(function () {
          me.loading = false
        })
    },

    asignarRol(rol) {
      if (rol.loading) return
      
      this.$set(rol, 'loading', true)
      
      const usuario = this.$store.state.usuario ? this.$store.state.usuario.user : 'sistema'
      
      // Usar la nueva API: DeterminarRol
      axios
        .put(`api/Usuarios/DeterminarRol/${this.idUsuario}/${rol.id}/${usuario}`)
        .then(response => {
          // Cambiar el estado local
          rol.estado = rol.estado === 'Asignado' ? 'No asignado' : 'Asignado'
          
          this.$store.dispatch('setSnackbar', {
            color: 'success',
            text: response.data || `Rol ${rol.estado === 'Asignado' ? 'asignado' : 'removido'} exitosamente`
          })
          
          // Emitir evento para actualizar contador
          this.$emit('rol-cambiado', {
            idUsuario: this.idUsuario,
            rolesAsignados: this.rolesAsignados
          })

          // Actualizar roles en el store si es el usuario actual
          if (this.idUsuario == this.$store.state.usuario.idUsuario) {
            this.$store.dispatch('actualizarRolesUsuario')
          }
        })
        .catch(error => {
          console.error('Error asignando rol:', error)
          this.$store.dispatch('setSnackbar', {
            color: 'error',
            text: 'Error al modificar el rol'
          })
        })
        .finally(() => {
          this.$set(rol, 'loading', false)
        })
    },

    quitarRol(rol) {
      // La API DeterminarRol maneja tanto asignar como quitar
      this.asignarRol(rol)
    },

    asignarTodosRoles() {
      if (this.loading) return
      
      this.loading = true
      const usuario = this.$store.state.usuario ? this.$store.state.usuario.user : 'sistema'
      
      // Usar la nueva API: AsignarRoles
      axios
        .put(`api/Usuarios/AsignarRoles/${this.idUsuario}/${usuario}`)
        .then(response => {
          // Actualizar estado local
          this.rolesUsuario.forEach(rol => {
            rol.estado = 'Asignado'
          })
          
          this.$store.dispatch('setSnackbar', {
            color: 'success',
            text: response.data || 'Todos los roles asignados exitosamente'
          })
          
          // Emitir evento para actualizar contador
          this.$emit('rol-cambiado', {
            idUsuario: this.idUsuario,
            rolesAsignados: this.rolesAsignados
          })

          // Actualizar roles en el store si es el usuario actual
          if (this.idUsuario == this.$store.state.usuario.idUsuario) {
            this.$store.dispatch('actualizarRolesUsuario')
          }
        })
        .catch(error => {
          console.error('Error asignando todos los roles:', error)
          this.$store.dispatch('setSnackbar', {
            color: 'error',
            text: 'Error al asignar todos los roles'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    quitarTodosRoles() {
      if (this.loading) return
      
      this.loading = true
      const usuario = this.$store.state.usuario ? this.$store.state.usuario.user : 'sistema'
      
      // Usar la nueva API: QuitarRoles
      axios
        .put(`api/Usuarios/QuitarRoles/${this.idUsuario}/${usuario}`)
        .then(response => {
          // Actualizar estado local
          this.rolesUsuario.forEach(rol => {
            rol.estado = 'No asignado'
          })
          
          this.$store.dispatch('setSnackbar', {
            color: 'success',
            text: response.data || 'Todos los roles removidos exitosamente'
          })
          
          // Emitir evento para actualizar contador
          this.$emit('rol-cambiado', {
            idUsuario: this.idUsuario,
            rolesAsignados: this.rolesAsignados
          })

          // Actualizar roles en el store si es el usuario actual
          if (this.idUsuario == this.$store.state.usuario.idUsuario) {
            this.$store.dispatch('actualizarRolesUsuario')
          }
        })
        .catch(error => {
          console.error('Error quitando todos los roles:', error)
          this.$store.dispatch('setSnackbar', {
            color: 'error',
            text: 'Error al quitar todos los roles'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    cerrarDialogoRoles() {
      this.dialog = false
      this.searchRoles = ''
    }
  }
}
</script>
